/**
 * Composant d'analytics pour les bundles
 * Affiche les statistiques de performance des bundles avec graphiques
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Table,
  Progress,
  Statistic,
  Empty,
  Spin
} from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  ChartBarIcon,
  EyeIcon,
  ShoppingCartIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import bundleService from '../../services/bundleService';
import { getDeviseActive } from '../../utils/devise';

const { Option } = Select;
const { RangePicker } = DatePicker;

const BundleAnalytics = ({ bundles }) => {
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({
    overview: {},
    performance: [],
    trends: [],
    topBundles: []
  });

  // Charger les analytics au montage et lors des changements
  useEffect(() => {
    loadAnalytics();
  }, [timeRange, selectedBundle, bundles]);

  /**
   * Charger les données d'analytics
   */
  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      // Calculer les statistiques générales à partir des bundles
      const overview = calculateOverviewStats(bundles);
      const performance = calculatePerformanceData(bundles);
      const trends = calculateTrendsData(bundles);
      const topBundles = calculateTopBundles(bundles);

      setAnalyticsData({
        overview,
        performance,
        trends,
        topBundles
      });
    } catch (error) {
      console.error('Erreur lors du chargement des analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Calculer les statistiques générales
   */
  const calculateOverviewStats = (bundles) => {
    const activeBundles = bundles.filter(b => b.status === 'active');
    
    let totalViews = 0;
    let totalAdditions = 0;
    let totalPurchases = 0;
    let totalRevenue = 0;

    bundles.forEach(bundle => {
      const analytics = bundle.bundle_analytics || [];
      analytics.forEach(analytic => {
        totalViews += analytic.views_count || 0;
        totalAdditions += analytic.additions_count || 0;
        totalPurchases += analytic.purchases_count || 0;
        totalRevenue += analytic.revenue_generated || 0;
      });
    });

    const conversionRate = totalViews > 0 ? (totalPurchases / totalViews * 100) : 0;
    const averageOrderValue = totalPurchases > 0 ? (totalRevenue / totalPurchases) : 0;

    return {
      totalBundles: bundles.length,
      activeBundles: activeBundles.length,
      totalViews,
      totalAdditions,
      totalPurchases,
      totalRevenue,
      conversionRate,
      averageOrderValue
    };
  };

  /**
   * Calculer les données de performance par bundle
   */
  const calculatePerformanceData = (bundles) => {
    return bundles.map(bundle => {
      const analytics = bundle.bundle_analytics || [];
      const views = analytics.reduce((sum, a) => sum + (a.views_count || 0), 0);
      const purchases = analytics.reduce((sum, a) => sum + (a.purchases_count || 0), 0);
      const revenue = analytics.reduce((sum, a) => sum + (a.revenue_generated || 0), 0);
      
      return {
        name: bundle.name,
        views,
        purchases,
        revenue,
        conversionRate: views > 0 ? (purchases / views * 100) : 0,
        status: bundle.status
      };
    }).sort((a, b) => b.revenue - a.revenue);
  };

  /**
   * Calculer les données de tendances (simulé)
   */
  const calculateTrendsData = (bundles) => {
    // Pour la démo, on génère des données simulées
    // En production, cela viendrait des vraies données analytics
    const days = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      days.push({
        date: date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        views: Math.floor(Math.random() * 100) + 50,
        purchases: Math.floor(Math.random() * 20) + 10,
        revenue: Math.floor(Math.random() * 5000) + 1000
      });
    }
    
    return days;
  };

  /**
   * Calculer le top des bundles
   */
  const calculateTopBundles = (bundles) => {
    return bundles
      .map(bundle => {
        const analytics = bundle.bundle_analytics || [];
        const revenue = analytics.reduce((sum, a) => sum + (a.revenue_generated || 0), 0);
        const purchases = analytics.reduce((sum, a) => sum + (a.purchases_count || 0), 0);
        
        return {
          key: bundle.id,
          name: bundle.name,
          type: bundle.bundle_type,
          status: bundle.status,
          revenue,
          purchases,
          items: bundle.bundle_items?.length || 0
        };
      })
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
  };

  // Configuration des couleurs pour les graphiques
  const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  // Colonnes pour le tableau des top bundles
  const topBundlesColumns = [
    {
      title: 'Bundle',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div className="font-medium">{text}</div>
          <div className="text-sm text-gray-500">
            {record.items} article{record.items > 1 ? 's' : ''} • {record.type}
          </div>
        </div>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          status === 'active' ? 'bg-green-100 text-green-800' :
          status === 'inactive' ? 'bg-red-100 text-red-800' :
          'bg-yellow-100 text-yellow-800'
        }`}>
          {status}
        </span>
      ),
    },
    {
      title: 'Achats',
      dataIndex: 'purchases',
      key: 'purchases',
      align: 'right',
    },
    {
      title: 'Revenus',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right',
      render: (value) => (
        <span className="font-medium">
          {new Intl.NumberFormat('fr-FR').format(value)} {getDeviseActive()}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Filtres */}
      <Card>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Select
              value={timeRange}
              onChange={setTimeRange}
              style={{ width: '100%' }}
            >
              <Option value="7d">7 derniers jours</Option>
              <Option value="30d">30 derniers jours</Option>
              <Option value="90d">3 derniers mois</Option>
              <Option value="1y">Année en cours</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Tous les bundles"
              value={selectedBundle}
              onChange={setSelectedBundle}
              style={{ width: '100%' }}
              allowClear
            >
              {bundles.map(bundle => (
                <Option key={bundle.id} value={bundle.id}>
                  {bundle.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      <Spin spinning={loading}>
        {/* Statistiques générales */}
        <Row gutter={16} className="mb-6">
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total bundles"
                value={analyticsData.overview.totalBundles}
                prefix={<ChartBarIcon className="h-4 w-4 text-blue-600" />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Vues totales"
                value={analyticsData.overview.totalViews}
                prefix={<EyeIcon className="h-4 w-4 text-purple-600" />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Achats totaux"
                value={analyticsData.overview.totalPurchases}
                prefix={<ShoppingCartIcon className="h-4 w-4 text-green-600" />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Revenus totaux"
                value={analyticsData.overview.totalRevenue}
                suffix={getDeviseActive()}
                prefix={<CurrencyDollarIcon className="h-4 w-4 text-orange-600" />}
                formatter={(value) => new Intl.NumberFormat('fr-FR').format(value)}
              />
            </Card>
          </Col>
        </Row>

        {/* Métriques avancées */}
        <Row gutter={16} className="mb-6">
          <Col xs={24} md={12}>
            <Card title="Taux de conversion">
              <Statistic
                value={analyticsData.overview.conversionRate}
                suffix="%"
                precision={2}
                valueStyle={{ color: '#3f8600' }}
                prefix={<ArrowTrendingUpIcon className="h-4 w-4" />}
              />
              <Progress 
                percent={Math.min(analyticsData.overview.conversionRate, 100)} 
                strokeColor="#3f8600"
                showInfo={false}
              />
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Panier moyen">
              <Statistic
                value={analyticsData.overview.averageOrderValue}
                suffix={getDeviseActive()}
                formatter={(value) => new Intl.NumberFormat('fr-FR').format(value)}
              />
            </Card>
          </Col>
        </Row>

        {/* Graphiques de tendances */}
        <Row gutter={16} className="mb-6">
          <Col xs={24} lg={16}>
            <Card title="Évolution des performances">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={analyticsData.trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'revenue' ? `${new Intl.NumberFormat('fr-FR').format(value)} ${getDeviseActive()}` : value,
                      name === 'views' ? 'Vues' : name === 'purchases' ? 'Achats' : 'Revenus'
                    ]}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="views" 
                    stroke={CHART_COLORS[0]} 
                    strokeWidth={2}
                    name="views"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="purchases" 
                    stroke={CHART_COLORS[1]} 
                    strokeWidth={2}
                    name="purchases"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card title="Répartition par statut">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Actifs', value: analyticsData.overview.activeBundles, color: CHART_COLORS[1] },
                      { name: 'Inactifs', value: analyticsData.overview.totalBundles - analyticsData.overview.activeBundles, color: CHART_COLORS[3] }
                    ]}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {[
                      { name: 'Actifs', value: analyticsData.overview.activeBundles, color: CHART_COLORS[1] },
                      { name: 'Inactifs', value: analyticsData.overview.totalBundles - analyticsData.overview.activeBundles, color: CHART_COLORS[3] }
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Performance par bundle */}
        <Row gutter={16} className="mb-6">
          <Col xs={24}>
            <Card title="Performance par bundle">
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={analyticsData.performance.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'revenue' ? `${new Intl.NumberFormat('fr-FR').format(value)} ${getDeviseActive()}` : value,
                      name === 'views' ? 'Vues' : name === 'purchases' ? 'Achats' : 'Revenus'
                    ]}
                  />
                  <Bar dataKey="views" fill={CHART_COLORS[0]} name="views" />
                  <Bar dataKey="purchases" fill={CHART_COLORS[1]} name="purchases" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Top bundles */}
        <Row gutter={16}>
          <Col xs={24}>
            <Card title="Top bundles par revenus">
              {analyticsData.topBundles.length > 0 ? (
                <Table
                  columns={topBundlesColumns}
                  dataSource={analyticsData.topBundles}
                  pagination={false}
                  size="middle"
                />
              ) : (
                <Empty description="Aucune donnée disponible" />
              )}
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default BundleAnalytics;